import React, { useState, useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
// import { cn } from "@/lib/utils"; // removed (no longer needed after switching to GeoJSON layer)
import "mapbox-gl/dist/mapbox-gl.css";
import { Region, fetchAllRegions } from "../services/environmentalDataService";
import { NaturalEvent } from "../services/extremeWeatherService";
import { useToast } from "@/components/ui/use-toast";

interface EarthGlobeProps {
  onRegionSelect: (regionId: string) => void;
  selectedRegion: string | null;
  filteredRegions?: Region[];
  hazardEvents?: Array<
    Pick<NaturalEvent, "id" | "title" | "categoryLabel" | "coordinates">
  >;
  disasterEvents?: Array<
    Pick<
      NaturalEvent,
      "id" | "title" | "categoryLabel" | "coordinates" | "alertLevel"
    >
  >;
}

const EarthGlobe: React.FC<EarthGlobeProps> = ({
  onRegionSelect,
  selectedRegion,
  filteredRegions,
  hazardEvents,
  disasterEvents,
}) => {
  const { toast } = useToast();

  const [regions, setRegions] = useState<Region[]>([]);
  const [hoveredRegion, setHoveredRegion] = useState<Region | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const rotationRef = useRef<number | null>(null);
  const userInteractingRef = useRef(false);
  // Always holds the latest onRegionSelect so stale Mapbox closures stay current
  const onRegionSelectRef = useRef(onRegionSelect);
  useEffect(() => {
    onRegionSelectRef.current = onRegionSelect;
  }, [onRegionSelect]);

  const mapToken =
    import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || "YOUR_MAPBOX_TOKEN";

  // ---------------------------
  // Rotation Logic (FIXED)
  // ---------------------------
  const startRotation = () => {
    if (rotationRef.current || !map.current) return;

    const rotate = () => {
      if (!map.current || userInteractingRef.current) return;

      const center = map.current.getCenter();
      center.lng = (center.lng + 0.03) % 360;

      map.current.setCenter(center); // FIXED (no easeTo)

      rotationRef.current = requestAnimationFrame(rotate);
    };

    rotationRef.current = requestAnimationFrame(rotate);
  };

  const stopRotation = () => {
    if (rotationRef.current) {
      cancelAnimationFrame(rotationRef.current);
      rotationRef.current = null;
    }
  };

  // ---------------------------
  // Load Regions
  // ---------------------------
  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const data = await fetchAllRegions();
        setRegions(data);
      } catch (error) {
        toast({
          title: "Error loading regions",
          description: "Failed to load region data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [toast]);

  // ---------------------------
  // Initialize Map (ONLY ONCE)
  // ---------------------------
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    mapboxgl.accessToken = mapToken;

    const instance = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/satellite-v9",
      projection: "globe",
      center: [0, 20],
      zoom: 1.5,
      pitch: 0,
      bearing: 0,
      maxZoom: 6,
    });

    instance.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true }),
      "top-right",
    );

    instance.on("load", () => {
      setMapLoaded(true);

      // Add GeoJSON source for regions
      instance.addSource("regions", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      // Load marker images (default + selected)
      instance.loadImage("/location.png", (error, defaultImage) => {
        if (error) throw error;
        if (!instance.hasImage("marker-default")) {
          instance.addImage("marker-default", defaultImage);
        }
      });

      instance.loadImage("/location.png", (error, selectedImage) => {
        if (error) throw error;
        if (!instance.hasImage("marker-selected")) {
          instance.addImage("marker-selected", selectedImage);
        }
      });

      // Add region points as a symbol layer
      instance.addLayer({
        id: "region-points",
        type: "symbol",
        source: "regions",
        layout: {
          // Switch icon based on selectedRegion
          "icon-image": [
            "case",
            ["==", ["get", "id"], selectedRegion],
            "marker-selected",
            "marker-default",
          ],

          // Dynamic sizing (similar to circle-radius)
          "icon-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            2,
            ["case", ["==", ["get", "id"], selectedRegion], 0.2, 0.2],
            6,
            ["case", ["==", ["get", "id"], selectedRegion], 0.9, 0.5],
            10,
            ["case", ["==", ["get", "id"], selectedRegion], 1.2, 0.7],
          ],

          "icon-anchor": "bottom",
          "icon-allow-overlap": true, // allow icons to overlap
        },
        paint: {
          // Optional color tint (works if icons are monochrome SVG)
          "icon-color": "#40e0d0",

          // Overall opacity
          "icon-opacity": 0.9,
        },
      });

      // Add hazard event points as a symbol layer using iceberg.png
      instance.addSource("hazard-events", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      instance.loadImage("/iceberg.png", (error, icebergImage) => {
        if (error) throw error;
        if (!instance.hasImage("marker-iceberg")) {
          instance.addImage("marker-iceberg", icebergImage);
        }

        instance.addLayer({
          id: "hazard-event-points",
          type: "symbol",
          source: "hazard-events",
          layout: {
            "icon-image": "marker-iceberg",
            "icon-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              2,
              0.15,
              6,
              0.45,
              10,
              0.7,
            ],
            "icon-anchor": "bottom",
            "icon-allow-overlap": true,
          },
          paint: {
            "icon-opacity": 0.95,
          },
        });
      });

      // Add disaster event points as a symbol layer using desaster.png
      instance.addSource("disaster-events", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      instance.loadImage("/desaster.png", (error, disasterImage) => {
        if (error) throw error;
        if (!instance.hasImage("marker-disaster")) {
          instance.addImage("marker-disaster", disasterImage);
        }

        instance.addLayer({
          id: "disaster-event-points",
          type: "symbol",
          source: "disaster-events",
          layout: {
            "icon-image": "marker-disaster",
            "icon-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              2,
              0.15,
              6,
              0.45,
              10,
              0.7,
            ],
            "icon-anchor": "bottom",
            "icon-allow-overlap": true,
          },
          paint: {
            "icon-opacity": 0.95,
          },
        });
      });

      // Click handler for disaster event points
      instance.on("click", "disaster-event-points", (e) => {
        const feature = e.features?.[0] as any;
        if (!feature) return;
        const eventId = feature.properties?.id;
        if (eventId) {
          userInteractingRef.current = true;
          stopRotation();
          onRegionSelectRef.current(`disaster:${eventId}`);
          setTimeout(() => {
            userInteractingRef.current = false;
            startRotation();
          }, 2000);
        }
      });

      instance.on("mouseenter", "disaster-event-points", () => {
        userInteractingRef.current = true;
        stopRotation();
        if (mapContainer.current) {
          mapContainer.current.style.cursor = "pointer";
        }
      });

      instance.on("mouseleave", "disaster-event-points", () => {
        userInteractingRef.current = false;
        startRotation();
        if (mapContainer.current) {
          mapContainer.current.style.cursor = "";
        }
      });

      // Click handler for region points — pause rotation briefly so user can interact
      instance.on("click", "region-points", (e) => {
        const feature = e.features?.[0] as any;
        if (!feature) return;
        const regionId = feature.properties?.id;
        if (regionId) {
          userInteractingRef.current = true;
          stopRotation();
          onRegionSelectRef.current(regionId);
          setTimeout(() => {
            userInteractingRef.current = false;
            startRotation();
          }, 2000);
        }
      });

      // Click handler for iceberg hazard points.
      instance.on("click", "hazard-event-points", (e) => {
        const feature = e.features?.[0] as any;
        if (!feature) return;
        const eventId = feature.properties?.id;
        if (eventId) {
          userInteractingRef.current = true;
          stopRotation();
          onRegionSelectRef.current(`iceberg:${eventId}`);
          setTimeout(() => {
            userInteractingRef.current = false;
            startRotation();
          }, 2000);
        }
      });

      // Hover handlers for popup and to pause rotation while hovering
      instance.on("mouseenter", "region-points", () => {
        userInteractingRef.current = true;
        stopRotation();
      });

      instance.on("mouseenter", "hazard-event-points", () => {
        userInteractingRef.current = true;
        stopRotation();
        if (mapContainer.current) {
          mapContainer.current.style.cursor = "pointer";
        }
      });

      instance.on("mouseleave", "hazard-event-points", () => {
        userInteractingRef.current = false;
        startRotation();
        if (mapContainer.current) {
          mapContainer.current.style.cursor = "";
        }
      });

      instance.on("mousemove", "region-points", (e) => {
        const feature = e.features?.[0] as any;
        if (!feature) return;
        setHoveredRegion({
          id: feature.properties.id,
          name: feature.properties.name,
          country: feature.properties.country,
          coordinates: feature.geometry.coordinates,
        } as Region);
      });

      instance.on("mouseleave", "region-points", () => {
        setHoveredRegion(null);
        userInteractingRef.current = false;
        startRotation();
      });

      startRotation();
    });

    // ---------------------------
    // Interaction Handling
    // Add pointer/mouse/touch listeners on the container so dragging works reliably
    // ---------------------------
    instance.on("movestart", () => {
      userInteractingRef.current = true;
      stopRotation();
    });

    instance.on("moveend", () => {
      userInteractingRef.current = false;
      startRotation();
    });

    const containerEl = mapContainer.current;
    if (containerEl) {
      // Ensure the container allows pointer interactions
      containerEl.style.pointerEvents = "auto";
      containerEl.style.touchAction = "pan-y pinch-zoom";

      const onMouseDown = (e: MouseEvent) => {
        userInteractingRef.current = true;
        stopRotation();
      };
      const onMouseMove = (e: MouseEvent) => {
        if (userInteractingRef.current) stopRotation();
      };
      const onMouseUp = (e: MouseEvent) => {
        userInteractingRef.current = false;
        startRotation();
      };

      const onTouchStart = (e: TouchEvent) => {
        userInteractingRef.current = true;
        stopRotation();
      };
      const onTouchEnd = (e: TouchEvent) => {
        userInteractingRef.current = false;
        startRotation();
      };

      containerEl.addEventListener("mousedown", onMouseDown);
      containerEl.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);

      containerEl.addEventListener("touchstart", onTouchStart, {
        passive: true,
      });
      window.addEventListener("touchend", onTouchEnd);

      // store cleanup so we can remove on unmount
      (instance as any).__customContainerCleanup = () => {
        try {
          containerEl.removeEventListener("mousedown", onMouseDown);
          containerEl.removeEventListener("mousemove", onMouseMove);
          window.removeEventListener("mouseup", onMouseUp);
          containerEl.removeEventListener("touchstart", onTouchStart as any);
          window.removeEventListener("touchend", onTouchEnd as any);
        } catch (e) {}
      };
    }

    map.current = instance;

    return () => {
      stopRotation();
      try {
        const cleanup = (instance as any).__customContainerCleanup;
        if (typeof cleanup === "function") cleanup();
      } catch (e) {}
      // remove map and clear reference to avoid stale `map.current` after unmount
      try {
        instance.remove();
      } catch (e) {}
      map.current = null;
    };
  }, [mapToken]);

  // ---------------------------
  // Marker Management
  // ---------------------------
  // New: update Mapbox GeoJSON source with regions and hazard event data
  const updateMapSources = (
    regionsToShow: Region[],
    hazardEventsToShow: Pick<
      NaturalEvent,
      "id" | "title" | "categoryLabel" | "coordinates"
    >[],
    disasterEventsToShow: Pick<
      NaturalEvent,
      "id" | "title" | "categoryLabel" | "coordinates" | "alertLevel"
    >[],
  ) => {
    if (!map.current) return;
    // guard: ensure the map's style is loaded and the source API is safe to call
    try {
      // isStyleLoaded may not exist on older mapbox-gl types in some environments
      if (typeof (map.current as any).isStyleLoaded === "function") {
        if (!(map.current as any).isStyleLoaded()) return;
      } else {
        // fallback: ensure getStyle exists and is an object
        const style = (map.current as any).getStyle?.();
        if (!style) return;
      }
    } catch (e) {
      // defensive: if anything goes wrong reading style, skip updating
      return;
    }

    let regionSrc: mapboxgl.GeoJSONSource | undefined;
    try {
      regionSrc = map.current.getSource("regions") as
        | mapboxgl.GeoJSONSource
        | undefined;
    } catch (e) {
      return;
    }
    if (regionSrc) {
      const geojson = {
        type: "FeatureCollection",
        features: regionsToShow.map((region) => ({
          type: "Feature",
          properties: {
            id: region.id,
            name: region.name,
            country: region.country,
          },
          geometry: {
            type: "Point",
            coordinates: region.coordinates,
          },
        })),
      };

      try {
        regionSrc.setData(geojson as any);
      } catch (e) {
        // If setData fails (race), ignore
      }
    }

    let hazardSrc: mapboxgl.GeoJSONSource | undefined;
    try {
      hazardSrc = map.current.getSource("hazard-events") as
        | mapboxgl.GeoJSONSource
        | undefined;
    } catch (e) {
      return;
    }
    if (hazardSrc) {
      const hazardGeoJSON = {
        type: "FeatureCollection",
        features: hazardEventsToShow
          .filter((ev) => ev.coordinates && ev.coordinates.length === 2)
          .map((event) => ({
            type: "Feature",
            properties: {
              id: event.id,
              title: event.title,
              category: event.categoryLabel,
            },
            geometry: {
              type: "Point",
              coordinates: event.coordinates,
            },
          })),
      };

      try {
        hazardSrc.setData(hazardGeoJSON as any);
      } catch (e) {
        // If setData fails (race), ignore
      }
    }

    let disasterSrc: mapboxgl.GeoJSONSource | undefined;
    try {
      disasterSrc = map.current.getSource("disaster-events") as
        | mapboxgl.GeoJSONSource
        | undefined;
    } catch (e) {
      return;
    }
    if (disasterSrc) {
      const disasterGeoJSON = {
        type: "FeatureCollection",
        features: disasterEventsToShow
          .filter((ev) => ev.coordinates && ev.coordinates.length === 2)
          .map((event) => ({
            type: "Feature",
            properties: {
              id: event.id,
              title: event.title,
              category: event.categoryLabel,
              alertLevel: event.alertLevel ?? "Yellow",
            },
            geometry: {
              type: "Point",
              coordinates: event.coordinates,
            },
          })),
      };

      try {
        disasterSrc.setData(disasterGeoJSON as any);
      } catch (e) {
        // If setData fails (race), ignore
      }
    }
  };

  // Update markers
  useEffect(() => {
    if (!mapLoaded) return;
    updateMapSources(
      filteredRegions || [],
      hazardEvents || [],
      disasterEvents || [],
    );
  }, [filteredRegions, hazardEvents, disasterEvents, mapLoaded]);

  // ---------------------------
  // Fly to Selected Region
  // ---------------------------
  useEffect(() => {
    if (!map.current || !selectedRegion) return;

    const region = regions.find((r) => r.id === selectedRegion);
    if (!region) return;

    stopRotation();

    map.current.flyTo({
      center: region.coordinates as [number, number],
      zoom: 3.5,
      pitch: 40,
      duration: 1500,
    });
  }, [selectedRegion, regions]);

  return (
    <div className="relative w-full h-full min-h-[500px] overflow-hidden rounded-xl border border-border bg-primary/5 dark:bg-[#272727]">
      {(isLoading || !mapLoaded) && (
        <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
          <span>Loading Earth Data...</span>
        </div>
      )}

      <div ref={mapContainer} className="w-full h-full" />

      {hoveredRegion && map.current && (
        <div
          className="absolute z-50 bg-background/90 border rounded-lg p-2 text-center"
          style={{
            left: `${map.current.project(hoveredRegion.coordinates).x}px`,
            top: `${map.current.project(hoveredRegion.coordinates).y}px`,
          }}
        >
          <div className="font-medium">{hoveredRegion.name}</div>
          <div className="text-xs text-muted-foreground">
            {hoveredRegion.country}
          </div>
        </div>
      )}
    </div>
  );
};

export default EarthGlobe;
